import { formatarDataHora } from '@/lib/formato';

import type { SituacaoDoEnvio } from './tipos';

/**
 * O convite de primeiro acesso (migração 0024), na parte que não depende de
 * tela: montar o endereço, ler o código de volta dele e dizer em que pé está o
 * acesso de cada pessoa.
 *
 * O código vai no FRAGMENTO do endereço (`#`), e não na busca (`?`): o
 * navegador não envia o fragmento ao servidor, então o código não fica em log
 * de acesso de ninguém no caminho. A página do convite o lê daqui e o manda no
 * corpo de um POST.
 */

const CODIGO = /^[0-9a-f]{32}$/;

/** O endereço que quem administra entrega à pessoa. A origem é a desta tela — domínio é configuração, não código. */
export function enderecoDoConvite(origem: string, codigo: string): string {
  return `${origem.replace(/\/+$/, '')}/primeiro-acesso#${codigo}`;
}

/** O código a partir do fragmento do endereço, ou nulo se não houver um código inteiro ali. */
export function codigoDoFragmento(fragmento: string): string | null {
  const codigo = fragmento.replace(/^#/, '').trim().toLowerCase();
  return CODIGO.test(codigo) ? codigo : null;
}

export interface AcessoDaPessoa {
  readonly ativo: boolean;
  readonly email: string | null;
  readonly tem_acesso: boolean;
  readonly convite_ate: string | null;
}

/**
 * Em que pé está o acesso da pessoa, em texto — a tela de governança o
 * escreve sempre com rótulo, nunca só com cor.
 *
 * A ordem das perguntas é a ordem em que o convite falharia: pessoa
 * desativada não entra, pessoa sem e-mail não tem o que digitar, e só depois
 * importa se há convite aberto.
 *
 * "Inativo" não existe no sistema (p.23); a tela chama a pessoa de
 * "Desativada". E a frase de quem já entra não repete o e-mail: ele já está na
 * coluna E-mail e no painel, e era o texto mais largo da tabela.
 */
export function situacaoDoAcesso(pessoa: AcessoDaPessoa, agora: Date): { readonly rotulo: string; readonly detalhe: string } {
  const convite = pessoa.convite_ate ? new Date(pessoa.convite_ate) : null;
  const conviteValido = convite !== null && convite.getTime() > agora.getTime();

  if (!pessoa.ativo) {
    return { rotulo: 'Sem acesso', detalhe: 'Pessoa desativada não entra no sistema. Reative-a para poder convidá-la.' };
  }
  if (!pessoa.email) {
    return { rotulo: 'Sem acesso', detalhe: 'Grave o e-mail da pessoa para poder convidá-la.' };
  }
  if (pessoa.tem_acesso && conviteValido) {
    return { rotulo: 'Entra no sistema', detalhe: `Convite de troca de senha aberto até ${formatarDataHora(convite)}.` };
  }
  if (pessoa.tem_acesso) {
    return { rotulo: 'Entra no sistema', detalhe: 'Entra com o e-mail cadastrado.' };
  }
  if (conviteValido) {
    return { rotulo: 'Convite aberto', detalhe: `Vale até ${formatarDataHora(convite)}. Ainda não foi aceito.` };
  }
  return { rotulo: 'Sem acesso', detalhe: 'Emita o convite para a pessoa escolher a senha e entrar.' };
}

/* ------------------------------------------------------------ o e-mail (0097) */

/** Quantas tentativas o worker faz antes de dar o envio por perdido (0097). */
export const TENTATIVAS_DO_ENVIO = 5;

export interface EnvioDoConvite {
  readonly email: string | null;
  /** Nulo quando o convite aberto não foi por e-mail (envio desligado, ou emitido antes de ligar). */
  readonly convite_envio: SituacaoDoEnvio | null;
  readonly convite_envio_tentativas: number | null;
  readonly convite_envio_erro: string | null;
  readonly convite_enviado_em: string | null;
}

/**
 * Em que pé está o e-mail do convite aberto — sempre com rótulo escrito, e o
 * detalhe diz o que fazer quando não saiu. Nulo quando não houve e-mail.
 */
export function situacaoDoEnvio(p: EnvioDoConvite): { readonly rotulo: string; readonly detalhe: string } | null {
  const para = p.email ?? 'o e-mail da pessoa';
  const tentativas = p.convite_envio_tentativas ?? 0;
  switch (p.convite_envio) {
    case 'enviado':
      return {
        rotulo: 'E-mail enviado',
        detalhe: p.convite_enviado_em
          ? `Enviado para ${para} em ${formatarDataHora(new Date(p.convite_enviado_em))}.`
          : `Enviado para ${para}.`,
      };
    case 'pendente':
      return tentativas === 0
        ? { rotulo: 'E-mail na fila', detalhe: `O convite sai por e-mail para ${para} em instantes.` }
        : {
            rotulo: 'E-mail não saiu',
            detalhe: `A tentativa ${String(tentativas)} de ${String(TENTATIVAS_DO_ENVIO)} falhou: ${p.convite_envio_erro ?? 'sem motivo informado'}. O sistema tenta de novo sozinho.`,
          };
    case 'falhou':
      return {
        rotulo: 'E-mail não enviado',
        detalhe: `As ${String(TENTATIVAS_DO_ENVIO)} tentativas falharam (${p.convite_envio_erro ?? 'sem motivo informado'}). Confira o e-mail da pessoa, emita outro convite e entregue o endereço a ela.`,
      };
    case 'cancelado':
      return { rotulo: 'E-mail cancelado', detalhe: `${p.convite_envio_erro ?? 'O envio foi cancelado'}.` };
    default:
      return null;
  }
}

/**
 * Como o convite chega à pessoa, dito antes de emitir. Com o envio desligado
 * neste ambiente, a tela diz isso — e o que fazer.
 */
export function fraseDaEntrega(porEmail: boolean, email: string | null): string {
  return porEmail
    ? `O convite vai por e-mail para ${email ?? 'a pessoa'}. Ela abre o endereço, escolhe a senha e entra. Você não fica sabendo a senha.`
    : 'O envio por e-mail não está ligado neste ambiente: depois de emitir, copie o endereço e entregue à pessoa.';
}

/** A mesma pergunta, curta, para a linha de lista do celular (M39). */
export function fraseCurtaDaEntrega(porEmail: boolean): string {
  return porEmail ? 'Vai por e-mail.' : 'Sem envio por e-mail neste ambiente: você entrega o endereço.';
}
